import React, { useState } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ConsultantRequestButton = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in both subject and message fields',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/consultant-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        
        toast({
          title: 'Request Sent Successfully',
          description: `Your request has been sent to ${result.admins_notified} system administrators and our sport performance consultant team.`
        });
        
        // Reset form and close modal
        setFormData({ subject: '', message: '' });
        setIsOpen(false);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send request');
      }
    } catch (error) {
      console.error('Error sending consultant request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send request. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Show button for players and coaches only
  if (!user || !['athlete', 'coach'].includes(user.role)) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors shadow-lg"
      >
        <MessageCircle className="w-4 h-4" />
        <span>Reach Out to Sport Performance Consultant</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                    <MessageCircle className="w-5 h-5 text-purple-600" />
                    <span>Request Sport Performance Consultant</span>
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">
                    Connect with our sport psychology experts for personalized guidance
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={loading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">What happens when you submit?</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• System administrators will be notified immediately</li>
                    <li>• Our sport psychology team at Grow Wellness Group will receive your request</li>
                    <li>• You'll be contacted within 24-48 hours to arrange consultation</li>
                    <li>• All consultations are confidential and professional</li>
                  </ul>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="What type of support are you looking for?"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    rows={6}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Please describe your situation, goals, and how our sport performance consultant can help you. The more details you provide, the better we can assist you."
                    required
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Include any specific challenges, performance goals, or mental training needs
                  </p>
                </div>

                {/* Submit Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    disabled={loading}
                  >
                    {loading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    <Send className="w-4 h-4" />
                    <span>{loading ? 'Sending...' : 'Send Request'}</span>
                  </button>
                </div>
              </form>

              {/* Contact Info */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Direct Contact</h4>
                  <p className="text-sm text-purple-800">
                    You can also reach our sport performance team directly at:{' '}
                    <a 
                      href="mailto:sports@growwellnessgroup.com" 
                      className="underline hover:no-underline"
                    >
                      sports@growwellnessgroup.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsultantRequestButton;